/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Turns whatever a failed batch write throws into something an admin can act on.
 *
 * The batch endpoint is unusually good at saying which operation failed and why
 * — `{error, failed_operation: {index, ref, table, reason}}`. That detail is
 * worth surfacing: "you are not allowed to create records in 'event'" tells an
 * admin exactly what to ask for, where "Forbidden" tells them nothing.
 *
 * It has to be dug out, though. APIHelper.apiCall throws on 401/403 rather than
 * returning the response, hanging the parsed body on `err.detail`, so a caller
 * that only inspects `resp.json()` never sees it.
 */

const SESSION_MESSAGE = 'Your session has expired. Sign in again and re-submit — nothing was saved.';

/** Pulls the batch's own error body out of an error, a detail, or a body. */
function findBody(source) {
  if (!source || typeof source !== 'object') return null;
  if (source.error || source.failed_operation) return source;
  if (source.detail && typeof source.detail === 'object') return findBody(source.detail);
  return null;
}

export function describeBatchError(error) {
  const fallback = {
    message: 'The event could not be saved. Nothing was written.',
    table: undefined,
    index: undefined,
    ref: undefined,
    reason: undefined,
  };

  if (!error) return fallback;

  if (error.type === 'session_expired') {
    return { ...fallback, message: SESSION_MESSAGE, reason: 'session_expired' };
  }

  const body = findBody(error);
  if (body) {
    const failed = body.failed_operation || {};
    return {
      message: body.error || fallback.message,
      table: failed.table,
      index: failed.index,
      ref: failed.ref,
      reason: failed.reason,
    };
  }

  // A string detail (for example "csrf_failed") is more informative than the
  // generic message APIHelper attached, so prefer it.
  if (typeof error.detail === 'string' && error.detail) {
    return { ...fallback, message: `${error.message || 'Request rejected'}: ${error.detail}` };
  }

  if (error.message) return { ...fallback, message: error.message };

  return fallback;
}
